import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { LatteButton, LatteSpinner, SEOHelmet } from '../../components/common';
import { BlogHeader } from '../../components/blog';
import { Footer } from '../../components/layout';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../styles/common/blog-components.css';

const BlogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [postId, setPostId] = useState(null); // 실제 post id 저장
  const [isAdmin, setIsAdmin] = useState(false);
  const [prevPost, setPrevPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // 최소 스와이프 거리 (픽셀)
  const minSwipeDistance = 50;

  useEffect(() => {
    loadPost();
    checkAdminStatus();
  }, [id]);

  const checkAdminStatus = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      setIsAdmin(data?.role === 'admin');
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const incrementViewCount = async (postId) => {
    try {
      if (postId) {
        await supabase.rpc('increment_blog_view_count', {
          post_id: postId
        });
      }
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const loadPost = async () => {
    try {
      // 먼저 id로 찾아보고, 없으면 slug로 찾기
      let query = supabase
        .from('blog_posts')
        .select('*');
      
      // UUID 형식인지 확인 (id인 경우)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      
      if (isUUID) {
        query = query.eq('id', id);
      } else {
        // UUID가 아니면 slug로 간주
        query = query.eq('slug', id);
      }
      
      const { data, error } = await query.single();

      if (error) {
        console.error('Error loading post:', error);
        // 404 에러를 반환하는 대신 404 페이지를 표시
        setPost(null);
        setLoading(false);
        return;
      }
      
      // slug가 있고 UUID로 접근한 경우 slug URL로 리다이렉트
      if (data.slug && isUUID) {
        navigate(`/blog/${data.slug}`, { replace: true });
        return;
      }
      
      setPost(data);
      setPostId(data.id); // 실제 id 저장
      incrementViewCount(data.id); // 포스트 로드 후 조회수 증가
      
      // 이전/다음 글 불러오기
      loadAdjacentPosts(data.created_at);
    } catch (error) {
      console.error('Error loading post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const loadAdjacentPosts = async (currentPostDate) => {
    try {
      // 이전 글 (현재 글보다 이전에 작성된 글 중 가장 최근 글)
      const { data: prevData } = await supabase
        .from('blog_posts')
        .select('id, title, slug')
        .lt('created_at', currentPostDate)
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (prevData) setPrevPost(prevData);

      // 다음 글 (현재 글보다 나중에 작성된 글 중 가장 오래된 글)
      const { data: nextData } = await supabase
        .from('blog_posts')
        .select('id, title, slug')
        .gt('created_at', currentPostDate)
        .eq('status', 'published')
        .order('created_at', { ascending: true })
        .limit(1)
        .single();
      
      if (nextData) setNextPost(nextData);
    } catch (error) {
      console.error('Error loading adjacent posts:', error);
    }
  };


  const handleDelete = async () => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postId); // 실제 post id 사용

      if (error) throw error;
      
      showSuccess('포스트가 삭제되었습니다');
      navigate('/blog');
    } catch (error) {
      console.error('Error deleting post:', error);
      showError('삭제에 실패했습니다');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}년 ${month}월 ${day}일`;
  };

  const getAttachmentFileName = (url) => {
    if (!url) return '';
    const parts = url.split('/');
    return decodeURIComponent(parts[parts.length - 1]);
  };

  // 터치 시작 핸들러
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // 터치 이동 핸들러
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  // 터치 종료 핸들러
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && nextPost) {
      // 왼쪽으로 스와이프 -> 다음 글로 이동
      navigate(`/blog/${nextPost.slug || nextPost.id}`);
    }
    if (isRightSwipe && prevPost) {
      // 오른쪽으로 스와이프 -> 이전 글로 이동
      navigate(`/blog/${prevPost.slug || prevPost.id}`);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <LatteSpinner size="lg" />
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 'var(--spacing-4)'
      }}>
        <p>포스트를 찾을 수 없습니다</p>
        <Link to="/blog">
          <LatteButton variant="primary">목록으로 돌아가기</LatteButton>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* SEO 메타 태그 */}
      {post && (
        <SEOHelmet
          title={post.title}
          description={post.summary || (post.content_html ? post.content_html.replace(/<[^>]*>/g, '').substring(0, 160) + '...' : '')}
          keywords={post.tags?.join(', ')}
          image={post.cover_image_url}
          url={`/blog/${post.slug || post.id}`}
          type="article"
          author={post.author_name}
          publishedTime={post.created_at}
          tags={post.tags}
        />
      )}
      
      <BlogHeader />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* 관리자 버튼 */}
        {isAdmin && (
          <div className="flex justify-end gap-2 mb-4">
            <Link to={`/blog/edit/${postId}`}>
              <LatteButton variant="outline" size="sm" className="border-amber-600 text-amber-600 hover:bg-amber-50">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                수정
              </LatteButton>
            </Link>
            <LatteButton 
              variant="danger" 
              size="sm"
              onClick={handleDelete}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              삭제
            </LatteButton>
          </div>
        )}

        {/* 제목 섹션 */}
        <article 
          className="bg-white touch-pan-y"
          style={{ touchAction: 'pan-y' }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <header className="py-8 border-b">
            {/* 목록으로 돌아가기 버튼 */}
            <Link to="/blog" className="inline-flex items-center text-gray-600 hover:text-amber-600 mb-4 transition-colors">
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7m0 0l7-7m-7 7h11" />
              </svg>
              <span className="text-sm">목록으로</span>
            </Link>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>
            
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>{formatDate(post.created_at)}</span>
              {post.author_name && (
                <>
                  <span className="text-gray-400">·</span>
                  <span>{post.author_name}</span>
                </>
              )}
              {post.view_count > 0 && (
                <>
                  <span className="text-gray-400">·</span>
                  <span>조회수 {post.view_count.toLocaleString()}</span>
                </>
              )}
            </div>
            
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {post.tags.map(tag => (
                  <Link 
                    key={tag} 
                    to={`/blog?tag=${tag}`} 
                    className="text-amber-600 hover:text-amber-700 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}
          </header>

          {/* 본문 */}
          <div className="py-8">
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content_html }}
            />
          </div>

          {/* 첨부파일 */}
          {post.attachment_url && (
            <div className="py-8 border-t">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">첨부파일</h3>
                <a 
                  href={post.attachment_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm transition-colors"
                  download
                >
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {getAttachmentFileName(post.attachment_url)}
                </a>
              </div>
            </div>
          )}

          {/* 이전/다음 글 네비게이션 */}
          <div className="py-8 border-t">
            {/* 모바일 스와이프 안내 */}
            <p className="text-xs text-gray-400 text-center mb-4 md:hidden">
              좌우로 스와이프하여 이전/다음 글로 이동할 수 있습니다
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 이전 글 */}
              <div className="flex justify-start">
                {prevPost ? (
                  <Link 
                    to={`/blog/${prevPost.slug || prevPost.id}`}
                    className="group flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-amber-600 transition-colors flex-1 max-w-sm"
                  >
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-gray-500 mb-1">이전 글</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-amber-600 line-clamp-2">
                        {prevPost.title}
                      </p>
                    </div>
                  </Link>
                ) : (
                  <div className="p-4 rounded-lg border border-gray-100 flex-1 max-w-sm">
                    <p className="text-xs text-gray-400 mb-1">이전 글</p>
                    <p className="text-sm text-gray-400">이전 글이 없습니다</p>
                  </div>
                )}
              </div>

              {/* 다음 글 */}
              <div className="flex justify-end">
                {nextPost ? (
                  <Link 
                    to={`/blog/${nextPost.slug || nextPost.id}`}
                    className="group flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-amber-600 transition-colors flex-1 max-w-sm"
                  >
                    <div className="text-right">
                      <p className="text-xs text-gray-500 mb-1">다음 글</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-amber-600 line-clamp-2">
                        {nextPost.title}
                      </p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                ) : (
                  <div className="p-4 rounded-lg border border-gray-100 flex-1 max-w-sm">
                    <p className="text-xs text-gray-400 mb-1">다음 글</p>
                    <p className="text-sm text-gray-400">다음 글이 없습니다</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </article>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogDetailPage;
